import { createSelector } from 'reselect'
import { identity, prop } from 'ramda'

import { hydrateListFromDictionary } from 'selectors/supportParameter'
import { getIsEditRoute, getRPRouteMatch } from 'selectors/router'

import { isSPValid } from 'utils/referenceParameter'
import { safeToJS, getInOr } from 'utils/immutable'
import createUri from 'utils/uriCreators'
import { PENDING, DESIGN } from 'constants/lifecycle'

export const selectReferenceParameter = (state) =>
  state.get('referenceParameter')

const selectSPDictionary = (state) =>
  state.getIn(['supportParameter', 'dictionary'])

export const selectRPBeingEdited = (state) =>
  safeToJS(state.getIn(['referenceParameter', 'rpBeingEdited']), null)
export const selectRPBeingEditedCompatibilityModules = (state) =>
  safeToJS(
    state.getIn([
      'referenceParameter',
      'rpBeingEdited',
      'compatibilityModules',
    ]),
    []
  )
const getRPDictionary = (state) =>
  state.getIn(['referenceParameter', 'dictionary'])

export const getRPBeingEdited = createSelector(selectRPBeingEdited, identity)

export const getCurrentRPEditing = createSelector(
  [selectRPBeingEdited, getIsEditRoute],
  (rpBeingEdited, isEditRoute) => Boolean(rpBeingEdited && isEditRoute)
)

export const getRPBeingEditedOrCurrentRP = createSelector(
  getRPBeingEdited,
  getRPRouteMatch,
  getRPDictionary,
  (rp, match, rpDictionary) => {
    if (rp) return rp
    if (rpDictionary.size === 0 || !match) return null

    const { url } = match
    const refparam = rpDictionary.get(url)
    return safeToJS(refparam)
  }
)

const getCurrentReferenceParameterData = (state) => {
  const rpState = selectReferenceParameter(state)

  return {
    rpBeingEdited: safeToJS(rpState.get('rpBeingEdited')),
    saveErrors: rpState.get('saveErrors'),
    isSearching: rpState.get('isSearching'),
    isValidForSaving:
      rpState.get('serviceDescriptionIsValid') &&
      rpState.get('itemDescriptionIsValid'),
  }
}

export const getRP = ({ refParameterNumber, releaseNumber, uri }) =>
  createSelector(getRPDictionary, (dictionary) => {
    const rp = dictionary.get(
      uri || `/reference-parameters/${refParameterNumber}/${releaseNumber}`
    )
    if (rp) return rp.toJS()
    return {
      supportParameters: [],
      removedSupportParameters: [],
      compatibilityModules: [],
      notes: [],
      refParameterNumber,
      releaseNumber,
    }
  })

export const selectRemovedSPsUris = (state) =>
  getInOr(
    [],
    ['referenceParameter', 'rpBeingEdited', 'removedSupportParameters']
  )(state)

export const getRemovedSPsUriSet = createSelector(
  selectRemovedSPsUris,
  (uris) => new Set(uris)
)

export const getRPBeingEditedHydrated = createSelector(
  [selectRPBeingEdited, selectSPDictionary],
  (rpBeingEdited, spDictionary) => {
    if (!rpBeingEdited) return null
    const supportParameters = hydrateListFromDictionary(
      spDictionary,
      rpBeingEdited.supportParameters
    )
    const addedSupportParameters = hydrateListFromDictionary(
      spDictionary,
      rpBeingEdited.addedSupportParameters
    )
    return {
      ...rpBeingEdited,
      supportParameters,
      addedSupportParameters,
      removedSupportParameters: rpBeingEdited.removedSupportParameters,
    }
  }
)

export const getRPByUri = createSelector(
  [
    getRPDictionary,
    selectSPDictionary,
    (_, props) => {
      if (!props) return null
      const { refParameterNumber, releaseNumber, uri } = props
      return (
        uri ||
        createUri.referenceParameter({
          refParameterNumber,
          releaseNumber,
        })
      )
    },
  ],
  (rpDictionary, spDictionary, uri) => {
    if (!uri) return null
    const rp = safeToJS(rpDictionary.get(uri))
    if (!rp) {
      return null
    }

    const supportParameters = hydrateListFromDictionary(
      spDictionary,
      rp.supportParameters
    )
    return {
      ...rp,
      supportParameters,
    }
  }
)

export const getRPStatus = createSelector(getRPByUri, (rp) =>
  rp ? rp.status : null
)

export const getPendingSPs = createSelector(getRPByUri, (rp) =>
  rp
    ? rp.supportParameters.filter(
        ({ status }) => status.includes(PENDING) || status.includes(DESIGN)
      )
    : null
)

export const getCurrentReferenceParameter = createSelector(
  [getCurrentReferenceParameterData],
  (referenceParameter) => referenceParameter
)

export const selectCurrentRPSupportParametersUriList = createSelector(
  getRPRouteMatch,
  getRPDictionary,
  (match, dictionary) =>
    safeToJS(dictionary.getIn([prop('url', match), 'supportParameters']), [])
)

export const selectRPBeingEditedSupportParametersUriList = (state) => {
  const rpState = selectReferenceParameter(state)
  const currentRP = rpState.get('rpBeingEdited')
  if (currentRP) {
    return [
      ...safeToJS(currentRP.get('supportParameters'), []),
      ...safeToJS(currentRP.get('addedSupportParameters'), []),
      ...safeToJS(currentRP.get('removedSupportParameters'), []),
    ]
  }
  return null
}

export const getCurrRPSPs = createSelector(
  selectRPBeingEditedSupportParametersUriList,
  (state, props) => selectSPDictionary(state, props),
  // ^^^ resolves weird circular dependency issue: https://github.com/reduxjs/reselect/issues/169
  (spUris, spDictionary) =>
    hydrateListFromDictionary(spDictionary || {}, spUris)
)
const getSelectedSourceRP = (state) => {
  const rpState = selectReferenceParameter(state)
  return rpState.get('selectedSourceRP')
}
const getSourceRPSPs = (state) => {
  const rpState = selectReferenceParameter(state)
  return rpState.get('importableSupportParameters')
}

const hydrateImportableSPs = (
  spDictionary,
  hydratedTargetSps,
  importableSpMetaDictionary
) => {
  const importableSpsDictionary = importableSpMetaDictionary.reduce(
    (dict, val, key) => {
      const jsVal = safeToJS(val)
      const jsSp = safeToJS(spDictionary.get(key))

      const isConflicted = isSPValid(jsSp, hydratedTargetSps)

      return [
        ...dict,
        {
          ...jsVal,
          ...jsSp,
          inConflict: isConflicted,
          action: isConflicted ? 'duplicate' : jsVal.action,
          disabled: !!hydratedTargetSps.find(({ uri }) => uri === key),
        },
      ]
    },
    []
  )

  return importableSpsDictionary
}

export const getImportInfo = createSelector(
  [
    getSelectedSourceRP,
    getSourceRPSPs,
    selectRPBeingEditedSupportParametersUriList,
    (state, props) => selectSPDictionary(state, props),
    // ^^^ resolves weird circular dependency issue: https://github.com/reduxjs/reselect/issues/169
  ],
  (sourceRP, importableSPs, spUris, spDictionary) => {
    const hydratedSPs = hydrateListFromDictionary(spDictionary, spUris)

    return {
      sourceRP: sourceRP ? sourceRP.toJS() : {},
      importableSPs: importableSPs
        ? hydrateImportableSPs(spDictionary, hydratedSPs, importableSPs)
        : null,
    }
  }
)

export const getRPList = ({ rpList: rpUriList }) =>
  createSelector(getRPDictionary, (dictionary) => {
    const uriSet = new Set(rpUriList)
    const rpList = dictionary.reduce(
      (
        coll,
        value,
        key // in this case reducing the dictionary is valid because the dictionary is an immutable Map
      ) => (uriSet.has(key) ? [...coll, safeToJS(value)] : coll),
      []
    )
    return rpList
  })

export const getCollectionNotification = (state) => {
  return safeToJS(state.getIn(['referenceParameter', 'collectionMessage']))
}
